// Shared interaction primitives for the admin portal.
//
// These cover the gaps in admin-ui.tsx (which has visual atoms but no
// interaction layer): modals, confirmation dialogs, toasts, a styled
// datetime field for scheduling, and a small anchored popover menu.
// All inline-styled, no new npm deps — matches the rest of the admin app.

import {
  createContext, useCallback, useContext, useEffect, useId, useMemo,
  useRef, useState, type CSSProperties, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { ghostBtn, primaryBtn, Icon } from "./admin-ui";

/* ─── Modal ──────────────────────────────────────────────────────── */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

export function Modal({ open, onClose, title, children, footer, width = 480 }: ModalProps) {
  const labelId = useId();
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the first input/button inside the surface for keyboard flow.
    const t = setTimeout(() => {
      const el = surfaceRef.current?.querySelector<HTMLElement>(
        "input, textarea, select, button:not([data-modal-close])"
      );
      el?.focus();
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      style={backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
    >
      <div ref={surfaceRef} style={{ ...surface, width }}>
        <div style={hdr}>
          <h2 id={labelId} style={hdrTitle}>{title}</h2>
          <button
            type="button"
            data-modal-close
            aria-label="Close"
            onClick={onClose}
            style={hdrClose}
          >
            {Icon.x}
          </button>
        </div>
        <div style={bodyStyle}>{children}</div>
        {footer && <div style={ftr}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ─── ConfirmDialog ──────────────────────────────────────────────── */

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone?: "primary" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open, title, body,
  confirmLabel = "Confirm", cancelLabel = "Cancel",
  confirmTone = "primary", busy = false,
  onConfirm, onCancel,
}: ConfirmDialogProps) {
  const confirmStyle: CSSProperties = confirmTone === "danger"
    ? { ...primaryBtn, background: "#7a2d1f" }
    : primaryBtn;
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onCancel}
      title={title}
      width={420}
      footer={
        <>
          <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button type="button" style={confirmStyle} onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
        {body}
      </div>
    </Modal>
  );
}

/* ─── Toasts ────────────────────────────────────────────────────── */

export type ToastTone = "success" | "error" | "info";

export interface ToastOpts {
  tone?: ToastTone;
  durationMs?: number;
}

interface ToastEntry {
  id: number;
  msg: string;
  tone: ToastTone;
  expiresAt: number;
}

interface ToastCtx {
  push: (msg: string, opts?: ToastOpts) => void;
}

const ToastContext = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastEntry[]>([]);
  const seq = useRef(0);

  const push = useCallback((msg: string, opts?: ToastOpts) => {
    const tone = opts?.tone ?? "info";
    const dur = opts?.durationMs ?? (tone === "error" ? 6000 : 3500);
    const id = ++seq.current;
    const expiresAt = Date.now() + dur;
    setItems(prev => {
      const next = [...prev, { id, msg, tone, expiresAt }];
      // Cap at 4 visible — drop oldest.
      return next.length > 4 ? next.slice(next.length - 4) : next;
    });
    window.setTimeout(() => {
      setItems(prev => prev.filter(t => t.id !== id));
    }, dur);
  }, []);

  const dismiss = useCallback((id: number) => {
    setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  const ctx = useMemo<ToastCtx>(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      {typeof document !== "undefined" && createPortal(
        <div style={toastTray}>
          {items.map(t => (
            <div
              key={t.id}
              style={{ ...toastBase, ...toneStyle(t.tone) }}
              onClick={() => dismiss(t.id)}
            >
              <span style={{ flex: 1 }}>{t.msg}</span>
              <span style={toastClose} aria-hidden="true">×</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): (msg: string, opts?: ToastOpts) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fall back to a no-op so a missing provider doesn't crash the app.
    // (Hard error in dev to make the bug obvious.)
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.error("useToast called outside <ToastProvider>");
    }
    return () => {};
  }
  return ctx.push;
}

/* ─── DateTimeField (native input wrapper) ───────────────────────── */

interface DateTimeFieldProps {
  value: string;            // ISO with SG offset, e.g. "2026-05-20T09:00:00+08:00"
  onChange: (iso: string) => void;
  min?: string;             // ISO
  max?: string;             // ISO
  label?: string;
}

// Convert ISO → "YYYY-MM-DDTHH:mm" in SG wall-time for the native input.
function isoToLocal(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Render in SG (UTC+8) wall-time regardless of viewer's TZ.
  const sg = new Date(d.getTime() + (8 * 60 + d.getTimezoneOffset()) * 60_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${sg.getFullYear()}-${pad(sg.getMonth() + 1)}-${pad(sg.getDate())}T${pad(sg.getHours())}:${pad(sg.getMinutes())}`;
}

// Convert "YYYY-MM-DDTHH:mm" (treated as SG wall-time) → ISO with +08:00.
function localToIso(local: string): string {
  if (!local) return "";
  // Append seconds + SG offset so the string is a valid RFC3339 timestamp.
  return `${local}:00+08:00`;
}

export function DateTimeField({ value, onChange, min, max, label }: DateTimeFieldProps) {
  const inputId = useId();
  return (
    <label htmlFor={inputId} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", fontWeight: 500 }}>
          {label}
        </span>
      )}
      <input
        id={inputId}
        type="datetime-local"
        value={isoToLocal(value)}
        min={min ? isoToLocal(min) : undefined}
        max={max ? isoToLocal(max) : undefined}
        onChange={(e) => onChange(localToIso(e.target.value))}
        style={inputBase}
      />
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)" }}>
        Singapore time (UTC+08:00)
      </span>
    </label>
  );
}

/* ─── PopoverMenu (anchored, click-outside to close) ──────────────── */

export interface PopoverItem {
  label: string;
  onClick: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
}

interface PopoverMenuProps {
  open: boolean;
  anchorRect: DOMRect | null;
  items: PopoverItem[];
  onClose: () => void;
}

export function PopoverMenu({ open, anchorRect, items, onClose }: PopoverMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onClick = () => onClose();
    document.addEventListener("keydown", onKey);
    // Defer attaching the click handler so the click that opened the
    // menu doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener("click", onClick), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  // Right-align under the trigger.
  const top = Math.min(anchorRect.bottom + 4, window.innerHeight - 200);
  const right = Math.max(window.innerWidth - anchorRect.right, 8);

  return createPortal(
    <div
      style={{ ...popoverWrap, top, right }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          disabled={it.disabled}
          onClick={() => {
            if (it.disabled) return;
            it.onClick();
            onClose();
          }}
          style={{
            ...popoverItem,
            color: it.tone === "danger" ? "#7a2d1f" : "var(--ink-2)",
            opacity: it.disabled ? 0.5 : 1,
            cursor: it.disabled ? "not-allowed" : "pointer",
          }}
        >
          {it.label}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/* ─── shared form input style (used by modals) ──────────────────── */

export const inputBase: CSSProperties = {
  padding: "8px 11px", borderRadius: 6,
  background: "var(--surface)", border: "1px solid var(--rule)",
  color: "var(--ink)", outline: "none",
  fontFamily: "var(--font-ui)", fontSize: 13,
};

/* ─── inline styles ──────────────────────────────────────────────── */

const backdrop: CSSProperties = {
  position: "fixed", inset: 0,
  background: "rgba(8, 10, 12, 0.45)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000, padding: 20,
  backdropFilter: "blur(2px)",
};
const surface: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--rule)",
  borderRadius: 10,
  boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35)",
  maxHeight: "calc(100vh - 40px)",
  display: "flex", flexDirection: "column",
  overflow: "hidden",
};
const hdr: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "14px 18px", borderBottom: "1px solid var(--rule)",
};
const hdrTitle: CSSProperties = {
  margin: 0, fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500,
  color: "var(--ink)", letterSpacing: "-0.015em",
};
const hdrClose: CSSProperties = {
  border: "none", background: "transparent", padding: 4, cursor: "pointer",
  color: "var(--ink-3)", display: "inline-flex", alignItems: "center",
  borderRadius: 4,
};
const bodyStyle: CSSProperties = {
  padding: "16px 18px", overflowY: "auto", flex: 1, minHeight: 0,
};
const ftr: CSSProperties = {
  display: "flex", justifyContent: "flex-end", gap: 8,
  padding: "12px 18px", borderTop: "1px solid var(--rule)",
  background: "var(--surface-2)",
};

const toastTray: CSSProperties = {
  position: "fixed", right: 18, bottom: 18,
  display: "flex", flexDirection: "column", gap: 8,
  zIndex: 1100, pointerEvents: "none",
};
const toastBase: CSSProperties = {
  pointerEvents: "auto", cursor: "pointer",
  minWidth: 260, maxWidth: 380,
  padding: "10px 14px", borderRadius: 8,
  display: "flex", alignItems: "center", gap: 10,
  fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 500,
  boxShadow: "0 12px 32px -10px rgba(0,0,0,0.30)",
  border: "1px solid",
};
function toneStyle(tone: ToastTone): CSSProperties {
  if (tone === "success") return { background: "#e0ebe2", borderColor: "#cad8cd", color: "#1d4a3a" };
  if (tone === "error")   return { background: "#f1dcd4", borderColor: "#e3c5bd", color: "#7a2d1f" };
  return                        { background: "var(--surface)", borderColor: "var(--rule)", color: "var(--ink)" };
}
const toastClose: CSSProperties = {
  fontSize: 16, lineHeight: 1, opacity: 0.6, fontWeight: 600,
};

const popoverWrap: CSSProperties = {
  position: "fixed",
  background: "var(--surface)", border: "1px solid var(--rule)",
  borderRadius: 8, boxShadow: "0 12px 28px -10px rgba(0,0,0,0.25)",
  padding: 4, minWidth: 160, zIndex: 1050,
  display: "flex", flexDirection: "column",
};
const popoverItem: CSSProperties = {
  textAlign: "left", border: "none", background: "transparent",
  padding: "7px 10px", borderRadius: 5,
  fontFamily: "var(--font-ui)", fontSize: 12.5,
};
